import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import type { IntegratedDashboardV8Item } from '@/api-client/metav8Over/model';

import {
  FlattenContext,
  HighlightTextSegment,
  MenuSearchResultItem,
} from '@/common/components/organisms/menuSearch/menuSearch.type';
import { ROOT_LEAF_MENU_PATH } from '@/common/components/organisms/menuSearch/menuSearch.define';
import { getMenuSearchItemIcon, isSectionTitle, lookupSubTitle } from './menuSearch.common.utils';

/**
 * path에서 dashboardId를 추출하는 함수
 * baseMenu.uses.ts의 useDashboardFavorite 로직 참고
 * @param path - 대시보드 path
 * @returns dashboardId (숫자) 또는 undefined
 */
const getDashboardId = (path: string | undefined): number | undefined => {
  if (!path) return undefined;

  try {
    // path가 상대 경로일 수 있으므로 URL 객체 사용
    // 예: /dashboard/123?rootMenu=kubernetes
    const url = new URL(path, 'http://localhost');
    const pathSegments = url.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    if (!lastSegment) return undefined;

    // 쿼리 파라미터가 포함된 경우 제거하고 숫자로 변환
    const dashboardId = Number(lastSegment.split('?')[0]);
    return Number.isNaN(dashboardId) ? undefined : dashboardId;
  } catch {
    // URL 파싱 실패 시 간단한 split 방식 시도
    // updateFavorite에서 사용하는 방식: split('/')[3]
    const segments = path.split('/');
    if (segments.length > 2) {
      const dashboardId = Number(segments[2]?.split('?')[0]);
      return Number.isNaN(dashboardId) ? undefined : dashboardId;
    }
    return undefined;
  }
};

/**
 * 텍스트에서 검색어를 하이라이팅하기 위한 세그먼트 배열 생성
 * @param text - 원본 텍스트
 * @param searchWord - 검색어
 * @returns 하이라이팅 정보가 포함된 텍스트 세그먼트 배열
 */
export const createHighlightTextSegments = (
  text: string,
  searchWord: string,
): HighlightTextSegment[] => {
  if (!searchWord || !text) {
    return [{ text, match: false }];
  }

  const lowerText = text.toLowerCase();
  const lowerSearchWord = searchWord.toLowerCase();

  // 검색어가 포함되지 않은 경우
  if (!lowerText.includes(lowerSearchWord)) {
    return [{ text, match: false }];
  }

  const result: HighlightTextSegment[] = [];
  let startPos = 0;
  const regexp = new RegExp(lowerSearchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');

  for (const matchValue of lowerText.matchAll(regexp)) {
    if (matchValue.index === undefined) {
      continue;
    }

    const matchLength = matchValue[0].length;

    // 매칭 전 텍스트 추가
    if (matchValue.index > startPos) {
      result.push({
        text: text.substring(startPos, matchValue.index),
        match: false,
      });
    }

    // 매칭된 텍스트 추가 (원본 텍스트의 대소문자 유지)
    result.push({
      text: text.substring(matchValue.index, matchValue.index + matchLength),
      match: true,
    });

    startPos = matchValue.index + matchLength;
  }

  // 남은 텍스트 추가
  if (startPos < text.length) {
    result.push({
      text: text.substring(startPos),
      match: false,
    });
  }

  return result;
};

/**
 * 재귀적으로 MenuInfo를 평탄화하여 MenuSearchResultItem 배열로 변환
 * 메뉴 타입과 대시보드 타입을 분리하여 처리
 * @param items - 평탄화할 MenuInfo 배열
 * @param context - 평탄화 컨텍스트
 * @param keyword - 검색 키워드 (선택)
 * @param indexCounter - 아이템 인덱스 카운터 (객체로 전달하여 참조 공유)
 * @param pathToSubTitleMap - path → subTitle 매핑 (lookup 용)
 * @returns 평탄화된 menuItems와 dashboardItems
 */
const flattenMenuItemsInternal = (
  items: MenuInfo[],
  context: FlattenContext = { isDashboard: false, isRootLevel: false },
  keyword?: string,
  indexCounter: { menu: number; dashboard: number } = { menu: 0, dashboard: 0 },
  pathToSubTitleMap?: Map<string, string>,
): {
  menuItems: MenuSearchResultItem[];
  dashboardItems: MenuSearchResultItem[];
} => {
  return items.reduce<{
    menuItems: MenuSearchResultItem[];
    dashboardItems: MenuSearchResultItem[];
  }>(
    (acc, item) => {
      const rootValue = context.rootValue || item.value;
      const isDashboard = context.isDashboard;
      // 최상단 depth의 text를 유지
      const rootParentText = context.rootParentText || item.text;
      const isRootLevel = context.isRootLevel ?? false;

      // 빈 text나 value를 가진 중간 depth 아이템은 결과에 추가하지 않음
      const isEmptyItem = !item.text || !item.value;

      // 검색 결과에서 제외할 중간 depth 메뉴 (섹션 타이틀)
      const isExcluded = isSectionTitle(item);

      // 최상단 레벨이 아닌 경우에만 현재 아이템을 결과에 추가
      // 빈 아이템과 검색 결과 제외 메뉴는 제외
      // path가 있는 아이템만 결과에 추가 (클릭 가능한 아이템만)
      // 홈 메뉴와 대시보드 메뉴는 root level이어도 특수 처리하여 추가
      // - 메뉴: 필터링 결과에 포함된 모든 아이템 추가 (keyword 체크 불필요)
      // - 대시보드/분석보드: 필터링 로직에서 이미 검색어에 매칭된 것만 포함되므로 모두 추가
      // isDashboard 컨텍스트에서는 루트 leaf 메뉴(대시보드 등)를 메뉴로 처리하지 않음
      const isRootLeafMenu = !isDashboard && item.value in ROOT_LEAF_MENU_PATH;
      const hasPath = !!item.path || isRootLeafMenu;

      // 대시보드 아이템 필터링
      let matchesKeyword = true;
      if (isDashboard) {
        const itemPath = item.path || '';
        const cleanPath = itemPath.split('?')[0];

        // path가 "/dashboard/list"인 대시보드 타입 항목은 항상 제외 (메뉴 항목이므로)
        if (cleanPath === '/dashboard/list') {
          matchesKeyword = false;
        } else if (keyword) {
          // keyword가 있으면 대시보드 이름과 비교
          const dashboardName = item.text?.toLowerCase() || '';
          const lowerKeyword = keyword.toLowerCase();
          matchesKeyword = dashboardName.includes(lowerKeyword);
        }
      }

      // 1-Depth 클릭 루트 메뉴(홈, 대시보드, AIOps)는 root level이어도 추가
      const shouldAddItem =
        (!isRootLevel || isRootLeafMenu) &&
        !isEmptyItem &&
        !isExcluded &&
        hasPath &&
        matchesKeyword;

      if (shouldAddItem) {
        // 현재 아이템 생성
        // 중복 방지를 위해 rootValue, item.value, index를 조합하여 unique id 생성
        const currentIndex = isDashboard ? indexCounter.dashboard++ : indexCounter.menu++;
        const uniqueId = isDashboard
          ? `${rootValue}_dashboard_${item.value}_${currentIndex}`
          : `${rootValue}_menu_${item.value}_${currentIndex}`;

        const icon = getMenuSearchItemIcon({
          isDashboard,
          rootValue,
        });

        // 1-Depth 클릭 루트 메뉴는 path가 없을 때 기본 경로로 보완
        const itemPath =
          item.path || (isRootLeafMenu ? ROOT_LEAF_MENU_PATH[item.value] : undefined);

        const resultItem: MenuSearchResultItem = {
          id: uniqueId,
          icon,
          featuredIcon: item.featuredIcon || undefined,
          depthTitle: rootParentText,
          title: item.text,
          type: isDashboard ? 'dashboard' : 'menu',
          path: itemPath,
          subTitle: isDashboard ? '' : lookupSubTitle(pathToSubTitleMap, itemPath),
          dashboardType: item.featuredIcon ? 'preset' : 'custom',
          dashboardSubType: context.dashboardSubType,
          dashboardId: isDashboard ? getDashboardId(itemPath) : undefined,
          favoriteId: isDashboard ? item.favoriteId : undefined,
        };

        // 타입에 따라 분리하여 추가
        if (isDashboard) {
          acc.dashboardItems.push(resultItem);
        } else {
          acc.menuItems.push(resultItem);
        }
      }

      // children 처리 (같은 rootParentText 유지, 더 이상 rootLevel이 아님)
      if (item.children && item.children.length > 0) {
        const childResults = flattenMenuItemsInternal(
          item.children,
          {
            isDashboard,
            parentText: item.text,
            rootValue,
            rootParentText,
            dashboardSubType: context.dashboardSubType,
            isRootLevel: false,
          },
          keyword,
          indexCounter,
          pathToSubTitleMap,
        );
        acc.menuItems.push(...childResults.menuItems);
        acc.dashboardItems.push(...childResults.dashboardItems);
      }

      // subMenuList 처리 (같은 rootParentText 유지, 더 이상 rootLevel이 아님)
      if (item.subMenuList && item.subMenuList.length > 0) {
        const subMenuResults = flattenMenuItemsInternal(
          item.subMenuList,
          {
            isDashboard,
            parentText: item.text,
            rootValue,
            rootParentText,
            dashboardSubType: context.dashboardSubType,
            isRootLevel: false,
          },
          keyword,
          indexCounter,
          pathToSubTitleMap,
        );
        acc.menuItems.push(...subMenuResults.menuItems);
        acc.dashboardItems.push(...subMenuResults.dashboardItems);
      }

      // dashboardList 처리 (dashboard 타입, rootParentText 유지)
      if (item.dashboardList) {
        const dashboardResults = flattenMenuItemsInternal(
          [item.dashboardList],
          {
            isDashboard: true,
            parentText: item.text,
            rootValue,
            rootParentText,
            dashboardSubType: 'dashboard',
            isRootLevel: false,
          },
          keyword,
          indexCounter,
          pathToSubTitleMap,
        );
        acc.dashboardItems.push(...dashboardResults.dashboardItems);
      }

      // analysisBoardList 처리 (dashboard 타입, rootParentText 유지)
      if (item.analysisBoardList) {
        const analysisBoardResults = flattenMenuItemsInternal(
          [item.analysisBoardList],
          {
            isDashboard: true,
            parentText: item.text,
            rootValue,
            rootParentText,
            dashboardSubType: 'analysisBoard',
            isRootLevel: false,
          },
          keyword,
          indexCounter,
          pathToSubTitleMap,
        );
        acc.dashboardItems.push(...analysisBoardResults.dashboardItems);
      }

      return acc;
    },
    { menuItems: [], dashboardItems: [] },
  );
};

/**
 * platform이 없는 커스텀 대시보드를 필터링하여 MenuInfo 배열로 변환
 * @param dashboardList - 대시보드 목록
 * @param searchWord - 검색어
 * @returns 필터링된 MenuInfo 배열
 */
export const filterCustomDashboardsWithoutPlatform = (
  dashboardList: IntegratedDashboardV8Item[],
  searchWord: string,
): MenuInfo[] => {
  if (!searchWord || !searchWord.trim()) {
    return [];
  }

  const keyword = searchWord.toLowerCase().trim();

  return dashboardList
    .filter((dashboard) => {
      // dashboardId와 dashboardName이 있어야 함
      if (!dashboard.dashboardId || !dashboard.dashboardName) {
        return false;
      }

      // menuPaths와 analysisMenuPaths가 모두 비어있거나 없는 경우만 필터링 (platform이 없는 커스텀 대시보드)
      // 빈 배열([])이거나 undefined이거나 null인 경우 모두 체크
      const menuPaths = dashboard.menuPaths;
      const analysisMenuPaths = dashboard.analysisMenuPaths;

      const hasMenuPaths = Array.isArray(menuPaths) && menuPaths.length > 0;
      const hasAnalysisMenuPaths = Array.isArray(analysisMenuPaths) && analysisMenuPaths.length > 0;

      // menuPaths나 analysisMenuPaths 중 하나라도 값이 있으면 제외
      if (hasMenuPaths || hasAnalysisMenuPaths) {
        return false;
      }

      // dashboardName과 searchWord를 비교
      const dashboardName = dashboard.dashboardName.toLowerCase().trim();
      return dashboardName.includes(keyword);
    })
    .map((dashboard) => {
      return {
        text: dashboard.dashboardName || '',
        value: `dashboard_${dashboard.dashboardId}`,
        path: `/dashboard/view/${dashboard.dashboardId}`,
        favoriteId: dashboard.favoriteId || undefined,
        featuredIcon: undefined,
        children: [],
      } as MenuInfo;
    });
};

/**
 * platform이 없는 커스텀 대시보드를 MenuInfo 배열로 변환
 * @param dashboardList - 대시보드 목록
 * @param searchWord - 검색어
 * @returns MenuInfo 배열 (빈 배열 또는 단일 MenuInfo를 가진 배열)
 */
export const getCustomDashboardsWithoutPlatformAsMenuInfo = (
  dashboardList: IntegratedDashboardV8Item[],
  searchWord: string,
): MenuInfo[] => {
  const filteredDashboards = filterCustomDashboardsWithoutPlatform(dashboardList, searchWord);

  if (filteredDashboards.length === 0) {
    return [];
  }

  // 모든 platform이 없는 커스텀 대시보드를 하나의 MenuInfo로 그룹화
  // dashboardList 필드에 subMenuList로 포함
  return [
    {
      text: 'Dashboard',
      value: 'dashboard_custom',
      dashboardList: {
        text: 'Dashboard',
        value: 'dashboard_custom',
        subMenuList: filteredDashboards,
      },
    },
  ];
};

/**
 * MenuInfo 배열을 평탄화하고 정렬
 * 1. 최상단 아이템은 제외하고 하위 요소들만 포함
 * 2. searchWord와 최상단 노드 text가 일치하면 해당 children 중 매칭되는 것만 반환
 * 3. 메뉴 타입을 먼저, 대시보드 타입을 나중에 배치
 * 4. 동일한 depthTitle끼리 그룹화
 * @param items - 평탄화할 MenuInfo 배열
 * @param searchWord - 검색어 (선택)
 * @param context - 평탄화 컨텍스트
 * @param pathToSubTitleMap - path → subTitle 매핑 (lookup 용)
 * @param customDashboards - platform이 없는 커스텀 대시보드 MenuInfo 배열
 * @returns 평탄화된 MenuSearchResultItem 배열
 */
export const flattenMenuItems = ({
  items,
  searchWord,
  context = { isDashboard: false, isRootLevel: true },
  pathToSubTitleMap,
  customDashboards,
}: {
  items: MenuInfo[];
  searchWord?: string;
  context: FlattenContext;
  pathToSubTitleMap?: Map<string, string>;
  customDashboards?: MenuInfo[];
}): MenuSearchResultItem[] => {
  let allMenuItems: MenuSearchResultItem[] = [];
  let allDashboardItems: MenuSearchResultItem[] = [];

  // 전체 평탄화 과정에서 공유할 인덱스 카운터
  const indexCounter = { menu: 0, dashboard: 0 };

  // searchWord가 있고 최상단 레벨인 경우
  // searchFilteringMenuItems는 이미 필터링된 결과이므로, 그대로 평탄화만 수행
  if (searchWord && context.isRootLevel) {
    items.forEach((item) => {
      const rootValue = item.value;
      const rootParentText = item.text;
      const isHomeMenu = item.value === BASE_MENU_VALUE.HOME;
      const isDashboardMenu = item.value === BASE_MENU_VALUE.DASHBOARD;
      const isAiopsMenu = item.value === BASE_MENU_VALUE.AIOPS;

      // 홈 메뉴는 children이 없어도 직접 추가
      if (isHomeMenu) {
        const currentIndex = indexCounter.menu++;
        const uniqueId = `${rootValue}_menu_${item.value}_${currentIndex}`;
        const icon = getMenuSearchItemIcon({
          isDashboard: false,
          rootValue,
        });
        const itemPath = item.path || ROOT_LEAF_MENU_PATH[BASE_MENU_VALUE.HOME];

        const resultItem: MenuSearchResultItem = {
          id: uniqueId,
          icon,
          featuredIcon: item.featuredIcon || undefined,
          depthTitle: rootParentText,
          title: item.text,
          type: 'menu',
          path: itemPath,
          subTitle: lookupSubTitle(pathToSubTitleMap, itemPath),
          dashboardType: item.featuredIcon ? 'preset' : 'custom',
          dashboardSubType: undefined,
          dashboardId: undefined,
          favoriteId: undefined,
        };

        allMenuItems.push(resultItem);
      }

      // 대시보드 메뉴는 children이 없어도 직접 추가
      if (isDashboardMenu && item.path) {
        const currentIndex = indexCounter.menu++;
        const uniqueId = `${rootValue}_menu_${item.value}_${currentIndex}`;
        const icon = getMenuSearchItemIcon({
          isDashboard: false,
          rootValue,
        });
        const itemPath = item.path || ROOT_LEAF_MENU_PATH[BASE_MENU_VALUE.DASHBOARD];

        const resultItem: MenuSearchResultItem = {
          id: uniqueId,
          icon,
          featuredIcon: item.featuredIcon || undefined,
          depthTitle: rootParentText,
          title: item.text,
          type: 'menu',
          path: itemPath,
          subTitle: lookupSubTitle(pathToSubTitleMap, itemPath),
          dashboardType: item.featuredIcon ? 'preset' : 'custom',
          dashboardSubType: undefined,
          dashboardId: undefined,
          favoriteId: undefined,
        };

        allMenuItems.push(resultItem);
      }

      // AIOps 메뉴는 children이 없어도 직접 추가
      if (isAiopsMenu) {
        const currentIndex = indexCounter.menu++;
        const uniqueId = `${rootValue}_menu_${item.value}_${currentIndex}`;
        const icon = getMenuSearchItemIcon({
          isDashboard: false,
          rootValue,
        });
        const itemPath = item.path || ROOT_LEAF_MENU_PATH[BASE_MENU_VALUE.AIOPS];

        const resultItem: MenuSearchResultItem = {
          id: uniqueId,
          icon,
          featuredIcon: item.featuredIcon || undefined,
          depthTitle: rootParentText,
          title: item.text,
          type: 'menu',
          path: itemPath,
          subTitle: lookupSubTitle(pathToSubTitleMap, itemPath),
          dashboardType: item.featuredIcon ? 'preset' : 'custom',
          dashboardSubType: undefined,
          dashboardId: undefined,
          favoriteId: undefined,
        };

        allMenuItems.push(resultItem);
      }

      // 이미 필터링된 결과이므로, children, subMenuList, dashboardList, analysisBoardList를 그대로 처리
      // children 처리
      if (item.children && item.children.length > 0) {
        const { menuItems, dashboardItems } = flattenMenuItemsInternal(
          item.children,
          {
            isDashboard: false,
            isRootLevel: false,
            rootValue,
            rootParentText,
          },
          searchWord, // searchWord를 전달하여 대시보드 이름 필터링
          indexCounter,
          pathToSubTitleMap,
        );
        allMenuItems.push(...menuItems);
        allDashboardItems.push(...dashboardItems);
      }

      // subMenuList 처리
      if (item.subMenuList && item.subMenuList.length > 0) {
        const { menuItems, dashboardItems } = flattenMenuItemsInternal(
          item.subMenuList,
          {
            isDashboard: false,
            isRootLevel: false,
            rootValue,
            rootParentText,
          },
          searchWord, // searchWord를 전달하여 대시보드 이름 필터링
          indexCounter,
          pathToSubTitleMap,
        );
        allMenuItems.push(...menuItems);
        allDashboardItems.push(...dashboardItems);
      }

      // dashboardList 처리
      if (item.dashboardList) {
        const { dashboardItems } = flattenMenuItemsInternal(
          [item.dashboardList],
          {
            isDashboard: true,
            isRootLevel: false,
            rootValue,
            rootParentText,
            dashboardSubType: 'dashboard',
          },
          searchWord, // searchWord를 전달하여 대시보드 이름 필터링
          indexCounter,
          pathToSubTitleMap,
        );
        allDashboardItems.push(...dashboardItems);
      }

      // analysisBoardList 처리
      if (item.analysisBoardList) {
        const { dashboardItems } = flattenMenuItemsInternal(
          [item.analysisBoardList],
          {
            isDashboard: true,
            isRootLevel: false,
            rootValue,
            rootParentText,
            dashboardSubType: 'analysisBoard',
          },
          searchWord, // searchWord를 전달하여 대시보드 이름 필터링
          indexCounter,
          pathToSubTitleMap,
        );
        allDashboardItems.push(...dashboardItems);
      }
    });
  } else {
    // searchWord가 없거나 rootLevel이 아닌 경우 기존 로직
    const { menuItems, dashboardItems } = flattenMenuItemsInternal(
      items,
      context,
      searchWord,
      indexCounter,
      pathToSubTitleMap,
    );
    allMenuItems = menuItems;
    allDashboardItems = dashboardItems;
  }

  // platform이 없는 커스텀 대시보드 처리
  // dashboardList는 getCustomDashboardsWithoutPlatformAsMenuInfo로 이미 필터링된 MenuInfo 배열
  // dashboardList 필드에 subMenuList를 가진 MenuInfo 구조를 처리
  if (customDashboards && Array.isArray(customDashboards) && customDashboards.length > 0) {
    customDashboards.forEach((menuItem) => {
      // dashboardList 필드가 있고 subMenuList가 있는 경우 처리
      if (menuItem.dashboardList && menuItem.dashboardList.subMenuList) {
        const { dashboardItems } = flattenMenuItemsInternal(
          menuItem.dashboardList.subMenuList,
          {
            isDashboard: true,
            isRootLevel: false,
            rootValue: menuItem.value || 'dashboard_custom',
            rootParentText: menuItem.text || 'Dashboard',
            dashboardSubType: 'dashboard',
          },
          searchWord, // searchWord를 전달하여 대시보드 이름 필터링 (이미 filterCustomDashboardsWithoutPlatform에서 필터링되었지만 추가 검증)
          indexCounter,
          pathToSubTitleMap,
        );
        allDashboardItems.push(...dashboardItems);
      }
    });
  }

  // 메뉴를 먼저, 대시보드를 나중에 합침
  const allItems = [...allMenuItems, ...allDashboardItems];

  // dashboardId 중복 제거 (같은 dashboardId를 가진 항목 중 첫 번째만 유지)
  const seenDashboardIds = new Set<number>();
  const uniqueItems = allItems.filter((item) => {
    // dashboardId가 없는 항목(메뉴 등)은 그대로 유지
    if (!item.dashboardId) {
      return true;
    }

    // 이미 본 dashboardId면 제외
    if (seenDashboardIds.has(item.dashboardId)) {
      return false;
    }

    // 처음 보는 dashboardId면 추가하고 유지
    seenDashboardIds.add(item.dashboardId);
    return true;
  });

  // depthTitle 기준으로 그룹화 (순서 유지하면서)
  const groupedByDepthTitle = new Map<string, MenuSearchResultItem[]>();
  const depthTitleOrder: string[] = [];

  uniqueItems.forEach((item) => {
    if (!groupedByDepthTitle.has(item.depthTitle)) {
      groupedByDepthTitle.set(item.depthTitle, []);
      depthTitleOrder.push(item.depthTitle);
    }
    groupedByDepthTitle.get(item.depthTitle)!.push(item);
  });

  // 그룹화된 결과를 평탄화
  return depthTitleOrder.reduce<MenuSearchResultItem[]>((acc, depthTitle) => {
    const items = groupedByDepthTitle.get(depthTitle) || [];
    acc.push(...items);
    return acc;
  }, []);
};
